import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DEFAULT_INSPECTION_TEMPLATE } from "@/data/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Save, Send, ArrowLeft, Download } from "lucide-react";
import { API } from "@/lib/api";
import TierBadge, { tierFor } from "@/components/TierBadge";

const buildFromTemplate = (tpl) =>
  tpl.map(sec => ({
    section: sec.section,
    items: (sec.items || []).map(item => ({
      item: typeof item === "string" ? item : (item.item || String(item)),
      score: 0,
      notes: "",
    })),
  }));

export default function InspectionForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [venueId, setVenueId] = useState(params.get("venue_id") || "");
  const [venue, setVenue] = useState(null);
  const [sections, setSections] = useState([]);
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState("draft");
  const [savedId, setSavedId] = useState(isNew ? null : id);
  const [busy, setBusy] = useState(false);
  const [templateLoaded, setTemplateLoaded] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/inspections/${id}`).then(r => {
        const d = r.data;
        setSections(d.sections?.length ? d.sections : buildFromTemplate(DEFAULT_INSPECTION_TEMPLATE));
        setSummary(d.summary_notes || "");
        setStatus(d.status);
        setVenueId(d.venue_id);
        setTemplateLoaded(true);
      }).catch(() => { toast.error("Inspection not found"); navigate("/dashboard"); });
    }
  }, [id, isNew, navigate]);

  useEffect(() => {
    if (venueId) api.get(`/venues/${venueId}`).then(r => setVenue(r.data)).catch(() => {});
  }, [venueId]);

  // For NEW inspections, load per-category template once we know the venue's category
  useEffect(() => {
    if (isNew && venue && !templateLoaded) {
      api.get(`/inspection-templates/${venue.category}`)
        .then(r => {
          const tpl = r.data?.sections?.length ? r.data.sections : DEFAULT_INSPECTION_TEMPLATE;
          setSections(buildFromTemplate(tpl));
        })
        .catch(() => setSections(buildFromTemplate(DEFAULT_INSPECTION_TEMPLATE)))
        .finally(() => setTemplateLoaded(true));
    }
  }, [isNew, venue, templateLoaded]);

  const updateItem = (si, ii, patch) =>
    setSections(secs => secs.map((s, i) => i !== si ? s : {
      ...s, items: s.items.map((it, j) => j !== ii ? it : { ...it, ...patch })
    }));

  const save = async (nextStatus) => {
    if (!venueId) { toast.error("No venue selected"); return; }
    setBusy(true);
    const payload = { venue_id: venueId, sections, summary_notes: summary, status: nextStatus };
    try {
      if (savedId) {
        await api.patch(`/inspections/${savedId}`, payload);
      } else {
        const { data } = await api.post("/inspections", payload);
        setSavedId(data.id);
      }
      setStatus(nextStatus);
      toast.success(nextStatus === "submitted" ? "Inspection submitted" : "Draft saved");
      if (nextStatus === "submitted") navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const totalItems = sections.reduce((n, s) => n + s.items.length, 0);
  const scoredItems = sections.reduce((n, s) => n + s.items.filter(i => i.score > 0).length, 0);
  const scoreSum = sections.reduce((n, s) => n + s.items.reduce((m, i) => m + (i.score || 0), 0), 0);
  const maxScore = totalItems * 5;
  const points = maxScore ? Math.round(scoreSum / maxScore * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-blue-700"><ArrowLeft className="w-4 h-4"/>Back to dashboard</Link>
      <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-gray-900">Inspection form</h1>
      {venue && <p className="mt-2 text-gray-700">Venue: <span className="font-semibold text-gray-900">{venue.venue_name}</span> — {venue.area}</p>}

      <div className="mt-4 flex items-center gap-3 flex-wrap text-sm">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 border border-gray-200"><span className="w-2 h-2 rounded-full bg-blue-600"/>Status: <strong className="capitalize">{status}</strong></span>
        <span className="text-gray-600">{scoredItems}/{totalItems} items scored · {scoreSum}/{maxScore} raw points</span>
        <TierBadge points={points} tier={tierFor(points)} testId="live-tier" />
      </div>

      <div className="mt-8 space-y-6">
        {sections.map((sec, si) => (
          <Card key={sec.section} className="border border-gray-200 rounded-xl">
            <CardContent className="p-6">
              <h2 className="font-heading text-xl font-semibold text-gray-900">{sec.section}</h2>
              <div className="mt-4 space-y-6">
                {sec.items.map((it, ii) => (
                  <div key={ii} className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
                    <Label className="text-base font-medium">{it.item}</Label>
                    <div className="mt-3 grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Score</span>
                          <span className="font-heading text-2xl font-bold text-blue-700">{it.score}<span className="text-gray-400 text-base font-medium">/5</span></span>
                        </div>
                        <Slider value={[it.score]} min={0} max={5} step={1}
                          onValueChange={([v]) => updateItem(si, ii, { score: v })}
                          className="mt-2" data-testid={`score-${si}-${ii}`}/>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Notes</Label>
                        <Textarea rows={2} value={it.notes} onChange={e => updateItem(si, ii, { notes: e.target.value })}
                          className="mt-1 border-2 border-gray-200 focus:border-blue-600" data-testid={`notes-${si}-${ii}`}/>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        <Card className="border-2 border-gray-900 rounded-xl">
          <CardContent className="p-6">
            <Label className="text-lg font-medium">Overall summary</Label>
            <Textarea rows={4} value={summary} onChange={e => setSummary(e.target.value)}
              className="mt-2 border-2 border-gray-300 focus:border-blue-600" data-testid="inspection-summary"/>
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-3 sticky bottom-4 bg-white border-2 border-gray-900 rounded-xl p-4">
          <Button disabled={busy} onClick={() => save("draft")} variant="outline" className="rounded-full border-2 border-gray-900" data-testid="save-draft-btn">
            <Save className="w-4 h-4 mr-2"/>Save draft
          </Button>
          <Button disabled={busy} onClick={() => save("submitted")} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="submit-inspection-btn">
            <Send className="w-4 h-4 mr-2"/>Submit inspection
          </Button>
          {savedId && (
            <Button asChild variant="outline" className="rounded-full border-2 border-gray-900 ml-auto" data-testid="download-pdf-btn">
              <a href={`${API}/inspections/${savedId}/pdf`} target="_blank" rel="noreferrer">
                <Download className="w-4 h-4 mr-2"/>Download PDF
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
